import type { RecursiveReadonly } from "./util.ts";

/** Called by a function listener to indicate that it is no longer listening
 *
 * Functions should gracefully terminate execution after this is called.
 */
export type Unsubscribe = () => void;

/** Called by a function to notify its listener(s) that an error has occurred */
export type ErrorHandler = (error: any) => void;

export type Shutdownable = { shutdown?: () => Promise<void> };

/** Represents an access-request workflow configuration in Firestore */
export type Workflow = {
  /** If true, allows approvers to self-approve requests */
  allowOneParty?: boolean;
  requireReason?: boolean;
};

export type P0User = RecursiveReadonly<{
  uid: string;
  email: string;
  /** Agents acting on behalf of the user
   *
   * With an AI workflow, the first actor is a MCP server, remaining actors are AI agents.
   */
  actors?: string[];
  provider?: string;
  // For private providers we trust ALL incoming users regardless of subject
  privateProvider?: boolean;
  groups?: string[];
  profile?: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    [key: string]: unknown;
  };
}>;

export type SlackAuthentication = {
  type: "slack";
};

export type TeamsAuthentication = {
  type: "teams";
};

export type WebexAuthentication = {
  type: "webex";
};

export type TokenAuthentication = {
  type: "token";
  tokenId: string;
};

export type AuthTrail =
  | SlackAuthentication
  | TeamsAuthentication
  | TokenAuthentication
  | WebexAuthentication;

export type AuthenticatedP0User = P0User & {
  readonly authTrail: Readonly<AuthTrail>;
};

export type ApiUser = {
  isApiUser: true;
  uid: string;
  name: string;
};

export const isApiUser = (
  user: ApiUser | AuthenticatedP0User | undefined
): user is ApiUser => !!user && "isApiUser" in user && user.isApiUser;

export const assertNeverError = (value: never) =>
  Object.assign(new Error("Got unexpected value of type never"), {
    value,
  });

export const assertNever = (value: never) => {
  throw assertNeverError(value);
};
